import React, { useEffect, useState, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { baseURL, powerStatsAPI, baseURLnoapi } from "../../Constants";
import { AuthContext } from "../../context/AuthContext";
import { useMappoolContext } from "../../context/MappoolContext";
import { FaGamepad, FaBug } from "react-icons/fa";
import "../../styles/admin/veto-page.scss";

// Types
interface Team {
	teamId: string;
	teamName: string;
	teamLogo: string;
	ownerId?: string | null;
	season: number;
	division?: string;
	twitter?: string | null;
	coachID?: string | null;
	website?: string | null;
	seed?: string;
	playoffSeed?: string;
}

interface Match {
	challonge_id: string;
	team1: Team;
	team2: Team;
	season: number;
	isPlayoff: boolean;
	playoffPlayed: boolean;
	stream_url: string;
	roundNumber: number; // Added roundNumber field
}

const VetoPage: React.FC = () => {
	const { matchId } = useParams<{ matchId: string }>();
	const navigate = useNavigate();
	const { isAdmin, token, user } = useContext(AuthContext);
	const { getMappool } = useMappoolContext();

	// Debug ref to track renders
	const renderCount = useRef(0);
	const [showDebug, setShowDebug] = useState<boolean>(false);

	// Debug logs
	useEffect(() => {
		renderCount.current += 1;
		if (match) {
		}
	});

	// Match and veto state
	const [match, setMatch] = useState<Match | null>(null);
	const [maps, setMaps] = useState<string[]>([]);
	const [isBo3, setIsBo3] = useState<boolean>(false);
	const [currentStep, setCurrentStep] = useState<number>(0);
	const [vetoHistory, setVetoHistory] = useState<
		{
			map: string;
			side?: string;
			type: string;
			team?: Team; // Added team information
		}[]
	>([]);
	const [selectedMaps, setSelectedMaps] = useState<Map<string, string>>(
		new Map()
	);
	const [selectedSides, setSelectedSides] = useState<Map<string, string>>(
		new Map()
	);
	const [isComplete, setIsComplete] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);
	const [showTeamSelection, setShowTeamSelection] = useState<boolean>(true);
	const [firstPickTeam, setFirstPickTeam] = useState<string>("");

	// Set page title
	useEffect(() => {
		if (match) {
			document.title = `Veto: ${match.team1.teamName} vs ${match.team2.teamName} | Admin - Northern Championships Series`;
		} else {
			document.title = 'Map Veto System | Admin - Northern Championships Series';
		}
	}, [match]);

	// Map helper
	const mapImages: Record<string, string> = {
		Mirage: "/images/maps/de_mirage.png",
		Nuke: "/images/maps/de_nuke.png",
		Inferno: "/images/maps/de_inferno.png",
		Dust2: "/images/maps/de_dust2.png",
		Overpass: "/images/maps/de_overpass.png",
		Ancient: "/images/maps/de_ancient.png",
		Anubis: "/images/maps/de_anubis.png",
		Train: "/images/maps/de_train.png",
		Vertigo: "/images/maps/de_vertigo.png",
	};

	// Helper function to get team logo URL, similar to TeamsPage.tsx
	const getTeamLogoUrl = (team: Team) => {
		if (team) {
			return `${baseURL}/Team/${team.teamId}/logo`;
		} else {
			// Return placeholder if no logo
			return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";
		}
	};

	// Determine if match is BO3 based on roundNumber
	const isMatchBo3 = (roundNumber: number): boolean => {
		const bo3Rounds = [5, 6, 9, 10, 11, 12, 13, 14];
		return bo3Rounds.includes(roundNumber);
	};

	// Load match data
	useEffect(() => {

		const fetchMatch = async () => {
			setLoading(true);
			if (!matchId) {
				return;
			}

			try {
				const matchResponse = await axios.get(
					`${powerStatsAPI}/Match/getUpcomingMatchById/${matchId}`
				);
				const matchData = matchResponse.data;

				if (matchData) {
					setMatch(matchData);

					// Determine if BO3 based on roundNumber
					const bo3 = isMatchBo3(matchData.roundNumber);
					setIsBo3(bo3);

					// Default first pick to team1
					setFirstPickTeam(matchData.team1.teamName);
				} else {
					console.error("No match data found.");
					navigate("/not-found");
				}
			} catch (error) {
				console.error("Error fetching match data:", error);
				navigate("/not-found");
			} finally {
				setLoading(false);
			}
		};

		fetchMatch();
	}, [matchId, navigate]);

	// Load mappool when match changes
	useEffect(() => {
		const loadMappool = async () => {
			if (match && match.season) {
				try {
					const seasonMappool = await getMappool(match.season);
					setMaps(seasonMappool);
					console.log(`Loaded mappool for season ${match.season}:`, seasonMappool);
				} catch (error) {
					console.error(`Error loading mappool for season ${match.season}:`, error);
					// Fallback to Season 2 mappool if loading fails
					const fallbackMaps = ["Ancient", "Anubis", "Dust2", "Inferno", "Mirage", "Nuke", "Train"];
					setMaps(fallbackMaps);
				}
			}
		};

		loadMappool();
	}, [match, getMappool]);

	// Define veto step interface
	interface VetoStep {
		team: string;
		action: "ban" | "pick" | "side" | "decider";
		requiresMap: boolean;
		requiresSide: boolean;
		mapReference?: number;
		isAutomatic?: boolean;
	}

	// Define veto steps based on BO1 or BO3 and first pick team
	const getVetoSteps = (): VetoStep[] => {
		if (!match) return [];

		const team1 = match.team1.teamName;
		const team2 = match.team2.teamName;

		// For BO1, we now use Team A and Team B based on the official Valve format
		// The firstPickTeam is now considered "Team A" in the Valve format
		const teamA = firstPickTeam === team1 ? team1 : team2;
		const teamB = firstPickTeam === team1 ? team2 : team1;

		return isBo3
			? [
					{
						team: teamA,
						action: "ban",
						requiresMap: true,
						requiresSide: false,
					},
					{
						team: teamB,
						action: "ban",
						requiresMap: true,
						requiresSide: false,
					},
					{
						team: teamA,
						action: "pick",
						requiresMap: true,
						requiresSide: false,
					},
					{
						team: teamB,
						action: "side",
						requiresMap: false,
						requiresSide: true,
						mapReference: 2,
					},
					{
						team: teamB,
						action: "pick",
						requiresMap: true,
						requiresSide: false,
					},
					{
						team: teamA,
						action: "side",
						requiresMap: false,
						requiresSide: true,
						mapReference: 4,
					},
					{
						team: teamB,
						action: "ban",
						requiresMap: true,
						requiresSide: false,
					},
					{
						team: teamA,
						action: "ban",
						requiresMap: true,
						requiresSide: false,
					},
					{
						team: "",
						action: "decider",
						requiresMap: true,
						requiresSide: false,
						isAutomatic: true,
					},
					{
						team: teamB,
						action: "side",
						requiresMap: false,
						requiresSide: true,
						mapReference: 8,
					},
			  ]
			: [
					// Official Valve BO1 format
					// Team A bans 2 maps
					{
						team: teamA,
						action: "ban",
						requiresMap: true,
						requiresSide: false,
					},
					{
						team: teamA,
						action: "ban",
						requiresMap: true,
						requiresSide: false,
					},
					// Team B bans 3 maps
					{
						team: teamB,
						action: "ban",
						requiresMap: true,
						requiresSide: false,
					},
					{
						team: teamB,
						action: "ban",
						requiresMap: true,
						requiresSide: false,
					},
					{
						team: teamB,
						action: "ban",
						requiresMap: true,
						requiresSide: false,
					},
					// Team A bans 1 more map
					{
						team: teamA,
						action: "ban",
						requiresMap: true,
						requiresSide: false,
					},
					// The last map is automatically selected
					{
						team: "",
						action: "decider",
						requiresMap: true,
						requiresSide: false,
						isAutomatic: true,
					},
					// Team B chooses starting side
					{
						team: teamB,
						action: "side",
						requiresMap: false,
						requiresSide: true,
						mapReference: 6,
					},
			  ];
	};

	const vetoSteps = getVetoSteps();

	// Helper to get team object from team name
	const getTeamByName = (teamName: string): Team | null => {
		if (!match) return null;
		if (match.team1.teamName === teamName) return match.team1;
		if (match.team2.teamName === teamName) return match.team2;
		return null;
	};

	// Handle team selection
	const handleTeamSelection = (teamName: string) => {
		setFirstPickTeam(teamName);
	};

	// Start the veto process
	const startVeto = () => {
		setShowTeamSelection(false);
	};

	// Get the current step information
	const getCurrentStep = (): VetoStep | null => {
		if (currentStep >= vetoSteps.length) {
			return null;
		}
		return vetoSteps[currentStep];
	};

	// Handle map selection
	const handleMapSelect = (map: string) => {
		const step = getCurrentStep();
		if (!step || !step.requiresMap || isMapSelected(map)) return;

		// For decider step, automatically select the last remaining map
		if (step.action === "decider") {
			const availableMaps = maps.filter((m: string) => !isMapSelected(m));
			if (availableMaps.length === 1) {
				// For decider, we don't have a specific team from the step,
				// but the last team to ban should be considered the picking team for decider
				const lastBanTeam =
					vetoHistory.length > 0
						? vetoHistory[vetoHistory.length - 1].team ?? null
						: null;

				addVetoStep(availableMaps[0], undefined, "decider", lastBanTeam);
			}
			return;
		}

		// Get the team that is making this selection
		const team = getTeamByName(step.team);

		// Add to history with team information
		addVetoStep(map, undefined, step.action, team);
	};

	// Handle side selection
	const handleSideSelect = (side: string) => {
		const step = getCurrentStep();
		if (!step || !step.requiresSide) return;

		// Get the team that is making this selection
		const team = getTeamByName(step.team);

		// Get the referenced map
		const referencedMapStep =
			step.mapReference !== undefined ? vetoHistory[step.mapReference] : null;
		if (!referencedMapStep) return;

		// Add to history with team information
		addVetoStep(referencedMapStep.map, side, "side", team);
	};

	// Add step to veto history
	const addVetoStep = (
		map: string,
		side: string | undefined,
		type: string,
		team: Team | null
	) => {

		// Add to history with team information
		const newHistory = [
			...vetoHistory,
			{ map, side, type, team: team || undefined },
		];
		setVetoHistory(newHistory);

		// Update selected maps and sides
		const newSelectedMaps = new Map(selectedMaps);

		// If this is a side selection, don't change the map status from "pick" to "side"
		if (type !== "side") {
			newSelectedMaps.set(map, type);
			setSelectedMaps(newSelectedMaps);
		}

		// Safely update selected sides
		if (side) {
			const newSelectedSides = new Map(selectedSides);
			// Verify this map doesn't already have a side, to prevent duplication
			if (!newSelectedSides.has(map)) {
				newSelectedSides.set(map, side);
				setSelectedSides(newSelectedSides);
			} else {
				console.warn(
					`Map ${map} already has side ${newSelectedSides.get(
						map
					)}, not adding ${side}`
				);
			}
		}

		// Move to next step
		const nextStep = currentStep + 1;
		setCurrentStep(nextStep);

		// Check if the veto is complete
		if (nextStep >= vetoSteps.length) {
			setIsComplete(true);
		} else if (vetoSteps[nextStep]?.isAutomatic) {
			// Handle automatic steps (like decider map)
			const availableMaps = maps.filter((m: string) => !isMapSelected(m));
			if (availableMaps.length === 1) {
				// Add a short delay and show a notification
				document
					.getElementById("map-" + availableMaps[0])
					?.classList.add("highlight-selection");

				setTimeout(() => {
					// For decider, use the team from the last ban
					const lastBanTeam =
						vetoHistory.length > 0
							? vetoHistory[vetoHistory.length - 1].team ?? null
							: null;

					addVetoStep(availableMaps[0], undefined, "decider", lastBanTeam);
				}, 1500);
			}
		}
	};

	// Check if a map is already selected
	const isMapSelected = (map: string): boolean => {
		return selectedMaps.has(map);
	};

	// Get the status of a map (banned, picked, decider)
	const getMapStatus = (map: string): string => {
		return selectedMaps.get(map) || "";
	};

	// Revert the last step
	const revertLastStep = () => {

		if (vetoHistory.length === 0) return;

		// If veto is complete, go back to the active step
		if (isComplete) {
			// Simply go back to the process page and keep the current veto state
			setIsComplete(false);
			return;
		}

		// Normal case: Remove the last step
		const newHistory = [...vetoHistory];
		const lastStep = newHistory.pop();

		setVetoHistory(newHistory);

		// Update selected maps and sides
		if (lastStep) {
			// For side selection, we don't want to remove the map from selectedMaps
			// Only remove from selectedMaps if not a side selection
			if (lastStep.type !== "side") {
				const newSelectedMaps = new Map(selectedMaps);
				newSelectedMaps.delete(lastStep.map);
				setSelectedMaps(newSelectedMaps);
			}

			// If it was a side selection, remove from selectedSides
			if (lastStep.side) {
				const newSelectedSides = new Map(selectedSides);
				newSelectedSides.delete(lastStep.map);
				setSelectedSides(newSelectedSides);
			}
		}

		// Move back to previous step
		setCurrentStep(prev => prev - 1);
	};

	// Generate fake timestamps for the veto log
	const generateFakeTimestamps = (count: number): string[] => {
		const baseTime = new Date(); // Use current time
		const timestamps = [];
		// Start with hour and minute, increment by 10 seconds each step
		const startHour = baseTime.getHours().toString().padStart(2, "0");
		const startMinute = baseTime.getMinutes().toString().padStart(2, "0");

		for (let i = 0; i < count; i++) {
			// Add 10 seconds for each step
			const seconds = (baseTime.getSeconds() + i * 10)
				.toString()
				.padStart(2, "0");
			timestamps.push(`${startHour}:${startMinute}:${seconds}`);
		}
		return timestamps;
	};

	// Save the veto log to the server
	const saveVetoLog = async () => {
		if (!match || vetoHistory.length === 0) return;

		const timestamps = generateFakeTimestamps(vetoHistory.length);

		// Format log string with each entry separated by line breaks
		let vetoLog = "";

		vetoHistory.forEach((step, index) => {
			const stepInfo = vetoSteps[index];
			const timestamp = timestamps[index];
			const teamName = step.team ? step.team.teamName : stepInfo.team;

			if (step.type === "ban") {
				vetoLog += `${timestamp} - ${teamName} removed ${step.map}\n`;
			} else if (step.type === "pick") {
				vetoLog += `${timestamp} - ${teamName} picked ${step.map}\n`;
			} else if (step.type === "side") {
				vetoLog += `${timestamp} - ${teamName} choose to start ${step.side} on ${step.map}\n`;
			} else if (step.type === "decider") {
				// If team information is available for decider, use it
				if (step.team) {
					vetoLog += `${timestamp} - ${step.team.teamName} got ${
						step.map
					} as the ${isBo3 ? "decider" : "last map"}\n`;
				} else {
					vetoLog += `${timestamp} - ${step.map} ${
						isBo3 ? "was left over" : "was picked"
					}\n`;
				}
			}
		});


		try {

			// Sending the veto log to the server
			await axios.post(`${powerStatsAPI}/Match/updateVetoLog`, null, {
				params: {
					matchId: match.challonge_id,
					season: match.season,
					vetoString: vetoLog,
				},
			});

			alert("Veto log saved successfully!");
			navigate(`/match/${matchId}`);
		} catch (error) {
			console.error("Error saving veto log:", error);
			alert("Failed to save the veto log.");
		}
	};

	// Get map image
	const getMapImage = (map: string): string => {
		return mapImages[map] || "/images/maps/default.jpg";
	};

	// Get team that performed the action on a map
	const getTeamForMap = (map: string): Team | null => {
		if (!match) return null;

		const historyItem = vetoHistory.find(
			(step) => step.map === map && step.type !== "side"
		);
		if (historyItem && historyItem.team) {
			return historyItem.team;
		}

		const historyIndex = vetoHistory.findIndex(
			(step) => step.map === map && step.type !== "side"
		);
		if (historyIndex === -1) return null;

		const stepInfo = vetoSteps[historyIndex];
		if (!stepInfo || !stepInfo.team) return null;

		return stepInfo.team === match.team1.teamName ? match.team1 : match.team2;
	};

	// Get team that chose side for a map
	const getTeamForSide = (map: string): Team | null => {
		if (!match) return null;

		const sideStep = vetoHistory.find(
			(step) => step.map === map && step.type === "side"
		);
		return sideStep?.team || null;
	};

	// Toggle debug window
	const toggleDebug = () => {
		setShowDebug(!showDebug);
	};

	// Render maps in tall format for both selection and complete view
	const renderMapsInTallFormat = () => {
		// For the complete view, we want to show maps in chronological order
		if (isComplete) {
			// Create an ordered list of maps based on when they were selected/banned
			const orderedMaps: string[] = [];
			const mapOrder: Map<string, number> = new Map();

			// First, create a map of map name to its order in the veto process
			vetoHistory.forEach((historyItem, index) => {
				// Only include each map once (skip side selections for maps already in the list)
				if (historyItem.type !== "side" && !mapOrder.has(historyItem.map)) {
					mapOrder.set(historyItem.map, index);
					orderedMaps.push(historyItem.map);
				}
			});

			// For any maps that weren't touched in the veto process, add them at the end
			maps.forEach((map) => {
				if (!mapOrder.has(map)) {
					orderedMaps.push(map);
				}
			});

			// Now render the maps in order
			return orderedMaps.map((map) => {
				const isSelected = isMapSelected(map);
				const status = getMapStatus(map);
				const team = getTeamForMap(map);
				const sideTeam = getTeamForSide(map);
				const side = selectedSides.get(map);

				// Get order number for label
				const orderNumber = mapOrder.has(map) ? mapOrder.get(map)! + 1 : "";

				const actionLabel =
					status === "ban"
						? "BANNED"
						: status === "pick"
						? "PICKED"
						: status === "decider"
						? "DECIDER"
						: "";

				return (
					<div
						key={map}
						id={`map-${map}`}
						className={`veto-map-tall ${isSelected ? status : ""}`}
					>
						<div className="map-image-tall">
							<img src={getMapImage(map)} alt={map} />

							{/* Order number at top */}
							{orderNumber && (
								<div className="map-order-number">{orderNumber}</div>
							)}

							{/* Side information at top */}
							{side && sideTeam && (
								<div className="map-side-info">
									<div
										className={`side-indicator ${
											side === "CT" ? "ct-side" : "t-side"
										}`}
									>
										{sideTeam.teamName} starts {side}
									</div>
								</div>
							)}

							{/* Map status and team at bottom */}
							{isSelected && (
								<div className={`map-status ${status}`}>
									<div className="status-label">{actionLabel}</div>
									{team && (
										<div className="team-info">
											<img
												src={getTeamLogoUrl(team)}
												alt={team.teamName}
												className="team-logo"
												onError={(e) => {
													(e.target as HTMLImageElement).src =
														"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";
												}}
											/>
											<span className="team-name" title={team.teamName}>
												{team.teamName}
											</span>
										</div>
									)}
								</div>
							)}
						</div>
						<div className={`map-name ${status}`}>{map}</div>
					</div>
				);
			});
		} else {
			// For the selection process, show all maps in the original order
			return maps.map((map) => {
				const isSelected = isMapSelected(map);
				const status = getMapStatus(map);
				const team = getTeamForMap(map);
				const sideTeam = getTeamForSide(map);
				const side = selectedSides.get(map);

				const actionLabel =
					status === "ban"
						? "BANNED"
						: status === "pick"
						? "PICKED"
						: status === "decider"
						? "DECIDER"
						: "";

				// Determine classes based on selection state and current step
				const step = getCurrentStep();
				const isSelectable = step?.requiresMap && !isSelected && !isComplete;
				const isHighlighted = step?.requiresMap && !isSelected && !isComplete;

				return (
					<div
						key={map}
						id={`map-${map}`}
						className={`veto-map-tall ${isSelectable ? "selectable" : ""} ${
							isHighlighted ? "highlighted" : ""
						} ${isSelected ? status : ""}`}
						onClick={() => isSelectable && handleMapSelect(map)}
						role="button"
						tabIndex={0}
						onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { if (isSelectable) handleMapSelect(map); } }}
					>
						<div className="map-image-tall">
							<img src={getMapImage(map)} alt={map} />

							{/* Side information at top */}
							{side && sideTeam && (
								<div className="map-side-info">
									<div
										className={`side-indicator ${
											side === "CT" ? "ct-side" : "t-side"
										}`}
									>
										{sideTeam.teamName} starts {side}
									</div>
								</div>
							)}

							{/* Map status and team at bottom */}
							{isSelected && (
								<div className={`map-status ${status}`}>
									<div className="status-label">{actionLabel}</div>
									{team && (
										<div className="team-info">
											<img
												src={getTeamLogoUrl(team)}
												alt={team.teamName}
												className="team-logo"
												onError={(e) => {
													(e.target as HTMLImageElement).src =
														"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";
												}}
											/>
											<span className="team-name" title={team.teamName}>
												{team.teamName}
											</span>
										</div>
									)}
								</div>
							)}

							{/* Overlay for selection */}
							{isSelectable && (
								<div className="map-select-overlay">
									<div className="select-message">
										{step?.action === "ban" ? "Click to Ban" : "Click to Pick"}
									</div>
								</div>
							)}
						</div>
						<div className={`map-name ${status}`}>{map}</div>
					</div>
				);
			});
		}
	};

	// Debugging return for auth issues
	if (!isAdmin) {
		return (
			<div className="veto-error">
				<h2>Admin Authentication Required</h2>
				<p>
					This page is for admins only. isAdmin value:{" "}
					{isAdmin ? "true" : "false"}
				</p>
				<p>Token exists: {token ? "Yes" : "No"}</p>
				<p>User exists: {user ? "Yes" : "No"}</p>
				<button onClick={() => navigate(`/match/${matchId}`)}>
					Back to Match
				</button>
			</div>
		);
	}

	// If we're loading match data, show a loading screen
	if (loading) {
		return (
			<div className="veto-loading">
				<div className="loading-spinner"></div>
				<h2>Loading match details...</h2>
				<p>IsAdmin: {isAdmin ? "true" : "false"}</p>
			</div>
		);
	}

	if (!match) {
		return (
			<div className="veto-error">
				<h2>Match not found</h2>
				<p>IsAdmin: {isAdmin ? "true" : "false"}</p>
				<a href={`/match/${matchId}`} className="back-button">
					Back to Match
				</a>
			</div>
		);
	}

	const currentStepInfo = getCurrentStep();

	return (
		<div className="veto-page">
			<div className="container">
				<div className="veto-header">
					<h1>Map Veto - {isBo3 ? "Best of 3" : "Best of 1"}</h1>
					<div className="teams-header">
						<div className="team team1">
							<img
								src={getTeamLogoUrl(match.team1)}
								alt={match.team1.teamName}
								className="team-logo"
								onError={(e) => {
									(e.target as HTMLImageElement).src =
										"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";
								}}
							/>
							<h3>{match.team1.teamName}</h3>
						</div>
						<div className="vs">VS</div>
						<div className="team team2">
							<h3>{match.team2.teamName}</h3>
							<img
								src={getTeamLogoUrl(match.team2)}
								alt={match.team2.teamName}
								className="team-logo"
								onError={(e) => {
									(e.target as HTMLImageElement).src =
										"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";
								}}
							/>
						</div>
					</div>
				</div>

				{showTeamSelection ? (
					// Team Selection View - Updated to reflect Valve format
					<div className="team-selection">
						<h2>Select first pick</h2>
						<div className="team-options">
							<div
								className={`team-option ${
									firstPickTeam === match.team1.teamName ? "selected" : ""
								}`}
								onClick={() => handleTeamSelection(match.team1.teamName)}
								role="button"
								tabIndex={0}
								onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleTeamSelection(match.team1.teamName); }}
							>
								<img
									src={getTeamLogoUrl(match.team1)}
									alt={match.team1.teamName}
									className="team-logo"
								/>
								<h3>{match.team1.teamName}</h3>
							</div>
							<div
								className={`team-option ${
									firstPickTeam === match.team2.teamName ? "selected" : ""
								}`}
								onClick={() => handleTeamSelection(match.team2.teamName)}
								role="button"
								tabIndex={0}
								onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleTeamSelection(match.team2.teamName); }}
							>
								<img
									src={getTeamLogoUrl(match.team2)}
									alt={match.team2.teamName}
									className="team-logo"
								/>
								<h3>{match.team2.teamName} as Team A</h3>
							</div>
						</div>
						<button className="start-veto-button" onClick={startVeto}>
							Start Veto Process
						</button>
					</div>
				) : (
					// Unified veto process view using tall maps for both active and complete states
					<div className={isComplete ? "veto-complete" : "veto-process"}>
						<div className="step-indicator">
							{isComplete ? (
								<h2 className="completion-indicator">Veto Complete!</h2>
							) : (
								<h2>
									Step {currentStep + 1}/{vetoSteps.length}:
									{currentStepInfo && (
										<>
											{currentStepInfo.team && (
												<span> {currentStepInfo.team}</span>
											)}
											{currentStepInfo.action === "ban" && " bans a map"}
											{currentStepInfo.action === "pick" && " picks a map"}
											{currentStepInfo.action === "side" && " chooses side"}
											{currentStepInfo.action === "decider" &&
												(isBo3 ? " Decider map" : " Pick map")}
										</>
									)}
								</h2>
							)}
						</div>

						{/* Show side selection if the current step requires it */}
						{currentStepInfo?.requiresSide ? (
							<div className="side-selection">
								<h3>Choose starting side for {currentStepInfo.team}:</h3>
								<div className="sides-container">
									<div
										className="side-option ct"
										onClick={() => handleSideSelect("CT")}
										role="button"
										tabIndex={0}
										onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSideSelect("CT"); }}
									>
										<img
											src="/images/maps/CT.png"
											alt="CT Side"
											onError={(e) => {
												console.error("Failed to load CT side image");
												(e.target as HTMLImageElement).src =
													"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%235d79ae'/%3E%3Ctext x='50' y='50' font-size='32' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ECT%3C/text%3E%3C/svg%3E";
											}}
										/>
										<span>CT</span>
									</div>
									<div
										className="side-option t"
										onClick={() => handleSideSelect("T")}
										role="button"
										tabIndex={0}
										onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSideSelect("T"); }}
									>
										<img
											src="/images/maps/T.png"
											alt="T Side"
											onError={(e) => {
												console.error("Failed to load T side image");
												(e.target as HTMLImageElement).src =
													"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23de9b35'/%3E%3Ctext x='50' y='50' font-size='32' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ET%3C/text%3E%3C/svg%3E";
											}}
										/>
										<span>T</span>
									</div>
								</div>
							</div>
						) : (
							// Always use the tall maps layout
							<div className="veto-maps-tall">{renderMapsInTallFormat()}</div>
						)}

						<div className="veto-controls">
							{isComplete ? (
								<>
									<button className="save-button" onClick={saveVetoLog}>
										Save Veto
									</button>
									<button className="revert-button" onClick={revertLastStep}>
										Go Back
									</button>
								</>
							) : (
								<>
									<button
										className="revert-button"
										onClick={revertLastStep}
										disabled={vetoHistory.length === 0}
									>
										Revert Last Step
									</button>
									<button className="debug-toggle" onClick={toggleDebug}>
										<FaBug /> {showDebug ? "Hide Debug" : "Show Debug"}
									</button>
								</>
							)}
						</div>

						{/* Debug information display - now toggleable */}
						{showDebug && (
							<div className="debug-info">
								<h3>Debug Info</h3>
								<div>
									<strong>Current Season:</strong> {match?.season || "Unknown"}
								</div>
								<div>
									<strong>Active Mappool:</strong> {maps.join(", ")}
								</div>
								<div>
									<strong>Selected Sides:</strong>{" "}
									{Array.from(selectedSides.entries())
										.map(([map, side]) => `${map}: ${side}`)
										.join(", ")}
								</div>
								<div>
									<strong>Veto History:</strong>{" "}
									{JSON.stringify(vetoHistory, null, 2)}
								</div>
								<div>
									<strong>Selected Maps:</strong>{" "}
									{Array.from(selectedMaps.entries())
										.map(([map, status]) => `${map}: ${status}`)
										.join(", ")}
								</div>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default VetoPage;
